/**
 * Plan des pistes façon plan papier de station (touche L, Échap pour
 * fermer). Panorama peint sur canvas (paint.ts), calque SVG interactif
 * (MapLayer.tsx), cartouche et légende (Cartouche.tsx), index, filtres
 * « où peut-on aller » et défauts du réseau (Side.tsx).
 *
 * Le panorama n'est recalculé qu'à l'ouverture, au redimensionnement et
 * quand un tracé, une remontée ou un bâtiment change ; le calque suit l'état
 * (ouvertures, travaux) à chaque battement de ui.tick.
 */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Maximize2, X, ZoomIn, ZoomOut } from 'lucide-preact';
import { DIFFICULTY_LABEL, LIFTS, SKILL_LEVELS, type Difficulty, type PisteNetwork, type Vec2 } from '@snowchain/shared';
import type { ClientStore } from '../../state/store';
import { focusEntity, hud } from '../context';
import { ui } from '../ui-state';
import { Cartouche, Legend, type CartoucheData } from './Cartouche';
import { MapLayer, type Emphasis, type HoverKind } from './MapLayer';
import { buildOverlay, LIFT_STATUS_LABEL, type Overlay } from './overlay';
import { paintPanorama, type PaintScene, type Painted } from './paint';
import { Side, type Skill } from './Side';
import { km, lengthLabel, meters, PISTE_COLOR } from './style';
import { domainOf, makeView, type View } from './view';
import './trailmap.css';

export function TrailMap() {
  if (!ui.trailMap.value) return null;
  return <TrailMapSheet />;
}

/** Empreinte de ce que le panorama peint (tracés, remontées, bâtiments). */
function geometryKey(store: ClientStore): string {
  const r = store.replica;
  if (!r) return '';
  let key = '';
  for (const p of r.pistes.values()) {
    let h = 0;
    for (const q of p.path) h = (h * 31 + Math.round(q[0]) * 7 + Math.round(q[1])) | 0;
    key += `p${p.id}:${p.width}:${p.path.length}:${h}|`;
  }
  for (const l of r.lifts.values()) key += `l${l.id}:${l.bottom.join(',')}:${l.top.join(',')}|`;
  for (const b of r.buildings.values()) key += `b${b.id}:${b.kind}:${Math.round(b.x)}:${Math.round(b.z)}|`;
  return key;
}

/** Scène à peindre pour une taille de vue. */
function buildScene(store: ClientStore, w: number, h: number): PaintScene {
  const r = store.replica;
  const v = store.map.features.village;
  const pts: Vec2[] = [
    [v.x - v.radius, v.z + v.radius * 0.6],
    [v.x + v.radius, v.z - v.radius],
  ];
  for (const l of r?.lifts.values() ?? []) pts.push(l.bottom, l.top);
  for (const p of r?.pistes.values() ?? []) for (const q of p.path) pts.push(q);
  const domain = domainOf(pts, [v.x, v.z], 120);
  const view = makeView(store.hf, domain, w, h);
  return {
    hf: store.hf,
    map: store.map,
    view,
    domain,
    ratio: Math.min(2, Math.max(1, window.devicePixelRatio || 1)),
    pistes: [...(r?.pistes.values() ?? [])].map((p) => ({ path: p.path, width: p.width })),
    lifts: [...(r?.lifts.values() ?? [])].map((l) => ({ bottom: l.bottom, top: l.top })),
    buildings: [...(r?.buildings.values() ?? [])].map((b) => ({ kind: b.kind, x: b.x, z: b.z })),
  };
}

function cartouche(store: ClientStore): CartoucheData {
  const r = store.replica;
  const hf = store.hf;
  const v = store.map.features.village;
  const byColor: Record<Difficulty, number> = { verte: 0, bleue: 0, rouge: 0, noire: 0 };
  let total = 0;
  let summit = hf.height(v.x, v.z);
  let pistesOpen = 0;
  for (const p of r?.pistes.values() ?? []) {
    byColor[p.difficulty]++;
    total += p.length;
    if (p.open) pistesOpen++;
    const s = p.path[0];
    if (s) summit = Math.max(summit, hf.height(s[0], s[1]));
  }
  let liftsOpen = 0;
  for (const l of r?.lifts.values() ?? []) {
    if (l.status === 'open') liftsOpen++;
    summit = Math.max(summit, hf.height(l.top[0], l.top[1]));
  }
  return {
    station: r?.station.name ?? store.map.stationName,
    date: r?.world.region.dateLabel ?? '',
    village: hf.height(v.x, v.z),
    summit,
    openKm: r?.station.stats.openKm ?? 0,
    totalKm: total / 1000,
    byColor,
    liftsOpen,
    liftsTotal: r?.lifts.size ?? 0,
    pistesOpen,
    pistesTotal: r?.pistes.size ?? 0,
  };
}

interface HoverState {
  kind: HoverKind;
  id: number | string;
  x: number;
  y: number;
}

interface Zoom {
  k: number;
  x: number;
  y: number;
}

function clampZoom(z: Zoom, w: number, h: number): Zoom {
  const k = Math.max(1, Math.min(4, z.k));
  return { k, x: Math.min(0, Math.max(w - w * k, z.x)), y: Math.min(0, Math.max(h - h * k, z.y)) };
}

/** Somme des longueurs atteintes (depuis l'abscisse d'entrée sur chaque piste). */
function reachedLength(net: PisteNetwork, pistes: Map<number, number>): number {
  let m = 0;
  for (const [id, s0] of pistes) {
    const info = net.pistes.get(id);
    if (info) m += Math.max(0, info.length - s0);
  }
  return m;
}

function TrailMapSheet() {
  const tick = ui.tick.value;
  const store = hud.store;
  const mapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [scene, setScene] = useState<{ view: View; painted: Painted } | null>(null);
  const [progress, setProgress] = useState<number | null>(0);
  const [skill, setSkill] = useState<Skill>(null);
  const [hover, setHover] = useState<HoverState | null>(null);
  const [zoom, setZoom] = useState<Zoom>({ k: 1, x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; zx: number; zy: number; moved: boolean } | null>(null);

  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setSize((s) => (s && s.w === Math.floor(r.width) && s.h === Math.floor(r.height) ? s : { w: Math.floor(r.width), h: Math.floor(r.height) }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const geoKey = store ? geometryKey(store) : '';
  const hasReplica = !!store?.replica;
  useEffect(() => {
    if (!store || !hasReplica || !size || size.w < 80 || size.h < 80) return;
    const job = { cancelled: false };
    const timer = setTimeout(
      () => {
        setProgress(0.01);
        const sc = buildScene(store, size.w, size.h);
        void paintPanorama(sc, job, (f) => {
          if (!job.cancelled) setProgress(f);
        }).then((painted) => {
          if (!painted || job.cancelled) return;
          setScene({ view: sc.view, painted });
          setZoom({ k: 1, x: 0, y: 0 });
          setProgress(null);
          (window as unknown as { __trailmap?: unknown }).__trailmap = { ms: Math.round(painted.ms), pw: painted.pw, ph: painted.ph };
        });
      },
      scene ? 250 : 20,
    );
    return () => {
      job.cancelled = true;
      clearTimeout(timer);
    };
  }, [geoKey, size?.w, size?.h, hasReplica]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !scene) return;
    c.width = scene.painted.pw;
    c.height = scene.painted.ph;
    c.getContext('2d')?.drawImage(scene.painted.canvas, 0, 0);
  }, [scene]);

  const overlay: Overlay | null = useMemo(() => (store && scene ? buildOverlay(store, scene.view, scene.painted) : null), [scene, tick]);
  const net = store?.network() ?? null;
  const issues = useMemo(() => net?.issues() ?? [], [net, tick]);

  // Accessibilité depuis le village pour le niveau choisi.
  const reachV = useMemo(() => (net && skill !== null ? net.reach('V', { skill, openOnly: true }) : null), [net, skill, tick]);
  const hotLift = hover?.kind === 'lift' ? (hover.id as number) : null;
  const hotPiste = hover?.kind === 'piste' ? (hover.id as number) : null;
  const reachTop = useMemo(() => (net && hotLift !== null ? net.reach(`T${hotLift}`, { skill: skill ?? undefined }) : null), [net, hotLift, skill, tick]);

  const emphasis: Emphasis = reachTop
    ? { pistes: new Set(reachTop.pistes.keys()), lifts: new Set([...reachTop.lifts, hotLift as number]), hotPiste: null, hotLift }
    : { pistes: reachV ? new Set(reachV.pistes.keys()) : null, lifts: reachV ? reachV.lifts : null, hotPiste, hotLift };

  const summary = useMemo(() => {
    const r = store?.replica;
    if (!r || !net) return '';
    if (skill === null) {
      let m = 0;
      for (const p of r.pistes.values()) m += p.length;
      return `Tout le domaine : ${r.pistes.size} piste${r.pistes.size > 1 ? 's' : ''}, ${km(m)} tracés. Choisissez un niveau pour voir ce qui est accessible depuis le village.`;
    }
    const lv = SKILL_LEVELS[skill];
    const n = reachV?.pistes.size ?? 0;
    const m = reachV ? reachedLength(net, reachV.pistes) : 0;
    return `${lv.label} : ${n} piste${n > 1 ? 's' : ''}, ${km(m)} accessibles depuis le village (${lv.hint}, pistes et remontées ouvertes).`;
  }, [net, skill, reachV, tick]);

  function pick(kind: 'piste' | 'lift' | 'building', id: number) {
    if (drag.current?.moved) return;
    focusEntity(kind, id);
    ui.trailMap.value = false;
  }

  function hoverFromMap(kind: HoverKind | null, id: number | string, e?: MouseEvent) {
    if (!kind) {
      setHover((h) => (h && h.id === id ? null : h));
      return;
    }
    const box = mapRef.current?.getBoundingClientRect();
    if (!box || !e) return;
    setHover({ kind, id, x: e.clientX - box.left, y: e.clientY - box.top });
  }

  function hoverFromIndex(kind: HoverKind | null, id: number) {
    if (!kind || !overlay) {
      setHover((h) => (h && h.id === id ? null : h));
      return;
    }
    let p: [number, number] | null = null;
    if (kind === 'piste') p = overlay.pistes.find((s) => s.id === id)?.badge ?? null;
    else if (kind === 'lift') {
      const l = overlay.lifts.find((s) => s.id === id);
      p = l ? (l.label ? [l.label.x, l.label.y] : l.top) : null;
    }
    const at = p ?? [40, 40];
    setHover({ kind, id, x: at[0] * zoom.k + zoom.x, y: at[1] * zoom.k + zoom.y });
  }

  function onWheel(e: WheelEvent) {
    if (!size) return;
    e.preventDefault();
    const box = mapRef.current?.getBoundingClientRect();
    if (!box) return;
    const mx = e.clientX - box.left;
    const my = e.clientY - box.top;
    setZoom((z) => {
      const k = Math.max(1, Math.min(4, z.k * Math.exp(-e.deltaY * 0.0015)));
      return clampZoom({ k, x: mx - ((mx - z.x) * k) / z.k, y: my - ((my - z.y) * k) / z.k }, size.w, size.h);
    });
  }

  function zoomBy(f: number) {
    if (!size) return;
    setZoom((z) => {
      const k = Math.max(1, Math.min(4, z.k * f));
      const cx = size.w / 2;
      const cy = size.h / 2;
      return clampZoom({ k, x: cx - ((cx - z.x) * k) / z.k, y: cy - ((cy - z.y) * k) / z.k }, size.w, size.h);
    });
  }

  function onPointerDown(e: PointerEvent) {
    if (e.button !== 0) return;
    drag.current = { x: e.clientX, y: e.clientY, zx: zoom.x, zy: zoom.y, moved: false };
  }
  function onPointerMove(e: PointerEvent) {
    const d = drag.current;
    if (!d || !size || !(e.buttons & 1)) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(dx, dy) < 4) return;
    d.moved = true;
    setHover(null);
    setZoom((z) => clampZoom({ k: z.k, x: d.zx + dx, y: d.zy + dy }, size.w, size.h));
  }
  function onPointerUp() {
    // Le clic qui suit un glisser est ignoré, puis l'état est remis à zéro.
    setTimeout(() => (drag.current = null), 0);
  }

  const card = store ? cartouche(store) : null;
  const loading = progress !== null;

  return (
    <div class="tm-root" role="dialog" aria-modal="true" aria-label="Plan des pistes">
      <div class="tm-sheet">
        <div
          class={`tm-map${zoom.k > 1 ? ' is-zoomed' : ''}`}
          ref={mapRef}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <div class="tm-stage" style={{ transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.k})` }}>
            {scene && <canvas ref={canvasRef} class="tm-canvas" style={{ width: `${scene.view.w}px`, height: `${scene.view.h}px` }} />}
            {scene && overlay && <MapLayer overlay={overlay} w={scene.view.w} h={scene.view.h} emphasis={emphasis} onHover={hoverFromMap} onPick={pick} />}
          </div>
          {card && <Cartouche c={card} />}
          <Legend />
          <div class="tm-zoom">
            <button type="button" onClick={() => zoomBy(1.4)} aria-label="Zoomer" title="Zoomer (molette)">
              <ZoomIn size={16} />
            </button>
            <button type="button" onClick={() => zoomBy(1 / 1.4)} aria-label="Dézoomer" title="Dézoomer">
              <ZoomOut size={16} />
            </button>
            <button type="button" onClick={() => setZoom({ k: 1, x: 0, y: 0 })} aria-label="Vue d'ensemble" title="Vue d'ensemble">
              <Maximize2 size={16} />
            </button>
          </div>
          {loading && (
            <div class={`tm-loading${scene ? ' is-refresh' : ''}`}>
              <span>Préparation du plan…</span>
              <i style={{ width: `${Math.round((progress ?? 0) * 100)}%` }} />
            </div>
          )}
          {!hasReplica && <div class="tm-loading">En attente des données de la station…</div>}
          {hover && store && net && <Tooltip hover={hover} store={store} net={net} skill={skill} reachTop={reachTop} overlay={overlay} />}
        </div>
        {overlay && (
          <Side
            pistes={overlay.pistes}
            lifts={overlay.lifts}
            issues={issues}
            skill={skill}
            summary={summary}
            hotPiste={hotPiste}
            hotLift={hotLift}
            reachPistes={reachTop ? emphasis.pistes : (reachV ? emphasis.pistes : null)}
            reachLifts={reachTop ? emphasis.lifts : (reachV ? emphasis.lifts : null)}
            onSkill={setSkill}
            onHover={hoverFromIndex}
            onPick={pick}
          />
        )}
        {!overlay && <aside class="tm-side" />}
        <button type="button" class="tm-close" onClick={() => (ui.trailMap.value = false)} aria-label="Fermer le plan" title="Fermer (Échap)">
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

function Tooltip({ hover, store, net, skill, reachTop, overlay }: { hover: HoverState; store: ClientStore; net: PisteNetwork; skill: Skill; reachTop: ReturnType<PisteNetwork['reach']> | null; overlay: Overlay | null }) {
  const r = store.replica;
  if (!r) return null;
  let body = null;
  if (hover.kind === 'piste') {
    const p = r.pistes.get(hover.id as number);
    const info = net.pistes.get(hover.id as number);
    const shape = overlay?.pistes.find((s) => s.id === hover.id);
    if (!p) return null;
    const works = (p.progress ?? 1) < 1;
    const state = works ? `En travaux (${Math.round((p.progress ?? 0) * 100)} %)` : p.open ? 'Ouverte' : `Fermée${p.closedReason ? ` : ${p.closedReason}` : ''}`;
    body = (
      <>
        <div class="tm-t-head">
          <span class="tm-t-num" style={{ background: PISTE_COLOR[p.difficulty] }}>
            {shape?.num ?? ''}
          </span>
          <strong>{p.name}</strong>
          <span class="tm-t-diff">Piste {DIFFICULTY_LABEL[p.difficulty].toLowerCase()}</span>
        </div>
        <div class="tm-t-line">
          {lengthLabel(p.length)} · dénivelé {meters(p.drop)}
        </div>
        <div class="tm-t-line">État : {state}</div>
        {info && (
          <div class="tm-t-line tm-t-muted">
            Départ : {net.label(info.from)} · Arrivée : {net.label(info.to)}
          </div>
        )}
      </>
    );
  } else if (hover.kind === 'lift') {
    const l = r.lifts.get(hover.id as number);
    const shape = overlay?.lifts.find((s) => s.id === hover.id);
    if (!l) return null;
    const n = reachTop?.pistes.size ?? 0;
    const m = reachTop ? reachedLength(net, reachTop.pistes) : 0;
    body = (
      <>
        <div class="tm-t-head">
          <strong>{l.name}</strong>
          <span class="tm-t-diff">{LIFTS[l.kind].label}</span>
        </div>
        {shape && (
          <div class="tm-t-line">
            {lengthLabel(shape.length)} · dénivelé {meters(shape.rise)}
          </div>
        )}
        <div class="tm-t-line">État : {LIFT_STATUS_LABEL[l.status]}</div>
        <div class="tm-t-line tm-t-muted">
          Depuis son sommet{skill !== null ? ` (${SKILL_LEVELS[skill].label.toLowerCase()})` : ''} : {n} piste{n > 1 ? 's' : ''}, {km(m)}
        </div>
      </>
    );
  } else {
    const p = overlay?.pictos.find((q) => q.key === hover.id);
    if (!p) return null;
    body = <div class="tm-t-head">{p.label}</div>;
  }
  const toLeft = hover.x > 260;
  const up = hover.y > (overlay ? overlay.h : 9999) - 190;
  const left = toLeft ? hover.x - 14 : hover.x + 14;
  return (
    <div class={`tm-tip${toLeft ? ' is-left' : ''}${up ? ' is-up' : ''}`} style={{ left: `${left}px`, top: `${up ? hover.y - 14 : hover.y + 14}px` }}>
      {body}
    </div>
  );
}
